'use client'

import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Container, Box, Alert, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

// Nicht display:none, das ignorieren manche Bots gezielt.
const honeypotStyle = {
    position: 'absolute',
    left: '-9999px',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
};

export default function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        betreff: '',
        message: ''
    });
    const [honeypot, setHoneypot] = useState('');
    const [feedback, setFeedback] = useState('');
    const [feedbackType, setFeedbackType] = useState('');
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [emailError, setEmailError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Zeitpunkt, an dem das Formular sichtbar wurde – Basis für die Mindestdauer.
    const openedAt = useRef(Date.now());

    const validateEmail = (email) => {
        if (!email) {
            return true;
        }
        const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;
        return re.test(String(email).toLowerCase());
    };

    useEffect(() => {
        const isEmailValid = validateEmail(formData.email);
        setEmailError(!isEmailValid);

        const isFormValid = formData.name && formData.email && isEmailValid && formData.betreff && formData.message;
        setIsButtonDisabled(!isFormValid);
    }, [formData]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    website: honeypot,
                    elapsed: Date.now() - openedAt.current,
                }),
            });

            const data = await response.json().catch(() => ({}));

            if (response.ok) {
                setFeedback(data.message || 'Nachricht erfolgreich übermittelt.');
                setFeedbackType('success');
                setFormData({
                    name: '',
                    email: '',
                    betreff: '',
                    message: ''
                });
                openedAt.current = Date.now();
                setTimeout(() => setFeedback(''), 5000);
            } else {
                setFeedback(data.message || 'Es gab ein Problem mit dem Senden Ihrer Nachricht.');
                setFeedbackType('error');
                setTimeout(() => setFeedback(''), 5000);
            }
        } catch (error) {
            setFeedback('Serverfehler beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut.');
            setFeedbackType('error');
            setTimeout(() => setFeedback(''), 5000);
        }

        setIsButtonDisabled(true);
        setIsLoading(false);
    };

    return (
        <Container maxWidth="sm">
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="name"
                    label="Ihre Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    color="secondary"
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Ihre E-Mail"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    color="secondary"
                    error={emailError} // Show error state for email field
                    helperText={emailError ? "Bitte geben Sie eine gültige E-Mail-Adresse ein." : ""} // Helper text on error
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="betreff"
                    label="Betreff"
                    name="betreff"
                    value={formData.betreff}
                    onChange={handleChange}
                    color="secondary"
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="message"
                    label="Ihre Nachricht"
                    name="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    color="secondary"
                />

                {/* Honeypot: für Menschen unsichtbar, Bots füllen ihn aus. */}
                <div style={honeypotStyle} aria-hidden="true">
                    <label htmlFor="website">Bitte nicht ausfüllen</label>
                    <input
                        type="text"
                        id="website"
                        name="website"
                        autoComplete="off"
                        tabIndex={-1}
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                    />
                </div>

                <Button
                    className='sendbutton'
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2, color: 'black', background: '#85DCC9' }}
                    endIcon={!isLoading ? <SendIcon /> : null} // Don't show the icon when loading
                    disabled={isButtonDisabled || isLoading} // Disable when submitting
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Nachricht senden'}
                </Button>

                {feedback && (
                    <Alert severity={feedbackType} sx={{ mt: 3 }}>
                        {feedback}
                    </Alert>
                )}
            </Box>
        </Container>
    );
}
